import type { components } from '../../api/generated/schema';
import type {
  CancelChangeResult,
  ContactMatch,
  ContactPoint,
  Me,
  PasswordChange,
  PendingChange,
  SocialConnection,
} from '../../domain/account';
import { toPersonRef } from '../mapping';

type S = components['schemas'];

export function toPendingChange(p: S['PendingChange']): PendingChange {
  return { newValue: p.new_value, expiresAt: p.expires_at, deliveryNotice: p.delivery_notice };
}

export function toMe(a: S['AccountProfile']): Me {
  return {
    id: a.id,
    status: a.status,
    username: a.username,
    firstName: a.first_name,
    lastName: a.last_name,
    email: a.email,
    emailVerified: a.email_verified,
    phone: a.phone,
    phoneVerified: a.phone_verified,
    avatarUrl: a.avatar_url,
    hasUsablePassword: a.has_usable_password,
    pendingEmailChange: a.pending_email_change ? toPendingChange(a.pending_email_change) : null,
    pendingPhoneChange: a.pending_phone_change ? toPendingChange(a.pending_phone_change) : null,
  };
}

export function toCancelChangeResult(c: S['CancelChange']): CancelChangeResult {
  const { cancelled, ...profile } = c;
  return { me: toMe(profile), cancelled };
}

export function toSocialConnection(c: S['SocialConnection']): SocialConnection {
  return { provider: c.provider, email: c.email, connectedAt: c.connected_at };
}

export function toPasswordChange(change: PasswordChange): S['PasswordChangeRequest'] {
  return {
    ...(change.currentPassword !== undefined && { current_password: change.currentPassword }),
    new_password: change.newPassword,
    confirm_new_password: change.confirmNewPassword,
  };
}

export function toContactPoint(p: S['ContactPoint']): ContactPoint {
  return {
    id: p.id,
    kind: p.kind,
    value: p.value,
    verifiedAt: p.verified_at,
    discoverable: p.discoverable,
  };
}

export function toContactMatch(m: S['ContactMatch']): ContactMatch {
  return {
    person: toPersonRef(m.person),
    matchedKind: m.matched.kind,
    matchedValue: m.matched.value,
  };
}
